// Reusable chrome for any HUD window (inventory, spellbook, vendor, etc.):
// drag-to-reposition by the header, close (×) button, click-to-front,
// per-window position persistence in localStorage, and a global lock
// toggle that freezes every draggable at once.
//
// Usage from a window class:
//
//   const header = setupWindow({
//     windowEl: this.root,
//     title: "Inventory",
//     storageKey: "ui.inventory.pos",
//     onClose: () => this.close(),
//   });
//   // then in render() insert `header` at the top of the window content
//
// Global helpers:
//   setWindowsLocked(true)        — freeze every draggable window
//   resetAllWindowPositions()     — wipe stored positions, send windows
//                                   back to their CSS defaults

import { el } from "./dom.js";

export interface WindowChromeOptions {
  windowEl: HTMLElement;
  title: string;
  // Per-window key; conventionally "ui.<name>.pos".
  storageKey: string;
  // Optional side-effect hook fired AFTER the chrome closes the window
  // (used to clear carts, hide tooltips, fire callbacks). Pure UX
  // windows (inventory, spellbook) with no side effects can omit this.
  onClose?: () => void;
  // false → suppress the X button in the chrome header. Default true.
  // Use for auto-managed windows (party panel) where a manual close
  // has no useful semantic.
  showCloseButton?: boolean;
}

interface WindowEntry {
  windowEl: HTMLElement;
  header: HTMLElement;
  storageKey: string;
  lockBtn: HTMLElement;
}

// Shared registry so global helpers (lock / reset) can iterate every
// window that registered itself via setupWindow.
const REGISTRY: WindowEntry[] = [];
let topZ = 30; // CSS default for all .*-panel/-window is z-index 30
let locked = false;

const POSITION_KEY_PREFIX = "ui.window.";

export interface WindowChromeHandle {
  header: HTMLElement;
  setTitle: (text: string) => void;
  // Visibility lifecycle — the chrome owns the `hidden` class toggling
  // and the isOpen flag so each window class doesn't need to redeclare
  // them. `close()` also fires the optional `onClose` side-effect hook.
  open: () => void;
  close: () => void;
  toggle: () => void;
  isOpen: () => boolean;
}

export function setupWindow(opts: WindowChromeOptions): WindowChromeHandle {
  // Lock button — toggles the GLOBAL lock state. Sits on every window
  // header so the user can lock from wherever they happen to be without
  // hunting for one specific button. State is mirrored across all
  // buttons because they all read/write the same `locked` flag.
  const lockBtn = el(
    "button",
    {
      class: "window-lock-btn",
      "aria-label": "Lock windows",
      type: "button",
      title: "Lock all windows (prevents accidental drag)",
    },
    "🔓",
  );
  lockBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    setWindowsLocked(!locked);
  });

  // Visibility state — chrome-owned so each window doesn't redeclare
  // isOpen/toggle/open/close boilerplate. Initial value reads from the
  // DOM so a window class that starts with `class="… hidden"` is
  // tracked correctly without extra plumbing.
  let isOpen = !opts.windowEl.classList.contains("hidden");

  const close = (): void => {
    if (!isOpen) return;
    isOpen = false;
    opts.windowEl.classList.add("hidden");
    if (opts.onClose) opts.onClose();
  };
  const open = (): void => {
    if (isOpen) return;
    isOpen = true;
    opts.windowEl.classList.remove("hidden");
  };
  const toggle = (): void => {
    if (isOpen) close();
    else open();
  };

  const titleEl = el("span", { class: "window-title" }, opts.title);
  const controls = el("div", { class: "window-controls" }, lockBtn);
  if (opts.showCloseButton !== false) {
    const closeBtn = el(
      "button",
      {
        class: "window-close-btn",
        "aria-label": "Close",
        type: "button",
        title: "Close (Esc)",
      },
      "×",
    );
    closeBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      close();
    });
    controls.append(closeBtn);
  }
  const header = el("header", { class: "window-header" }, titleEl, controls);

  REGISTRY.push({
    windowEl: opts.windowEl,
    header,
    storageKey: opts.storageKey,
    lockBtn,
  });

  restorePosition(opts.windowEl, opts.storageKey);
  attachDrag(opts.windowEl, header, opts.storageKey);
  attachBringToFront(opts.windowEl);
  // Make sure a freshly-registered button reflects the current global state
  // (in case the user locked, then opened a new window).
  refreshLockIcon(lockBtn);

  return {
    header,
    setTitle: (text: string) => {
      titleEl.textContent = text;
    },
    open,
    close,
    toggle,
    isOpen: () => isOpen,
  };
}

function refreshLockIcon(btn: HTMLElement): void {
  btn.textContent = locked ? "🔒" : "🔓";
  btn.classList.toggle("locked", locked);
  btn.setAttribute(
    "title",
    locked ? "Unlock windows" : "Lock all windows (prevents accidental drag)",
  );
}

// Persist position as { left, top } in pixels relative to the viewport.
// We switch the element to left/top anchoring on first drag — CSS
// defaults like `right: 24px` are cleared so the position math is
// unambiguous.
function restorePosition(windowEl: HTMLElement, storageKey: string): void {
  try {
    const raw = localStorage.getItem(POSITION_KEY_PREFIX + storageKey);
    if (!raw) return;
    const { left, top } = JSON.parse(raw) as { left: number; top: number };
    applyPosition(windowEl, left, top);
  } catch {
    // Corrupt entry — ignore and let CSS defaults take over.
  }
}

function applyPosition(windowEl: HTMLElement, left: number, top: number): void {
  // `left`/`top` here are VIEWPORT coordinates (what getBoundingClientRect
  // returns, what pointer events use). But `style.left`/`style.top` for an
  // absolutely-positioned element are relative to its **offsetParent**
  // (the nearest positioned ancestor). Our HUD windows live inside
  // `<main class="world-stage">` which has `position: relative`, so the
  // offsetParent is that wrapper — typically offset ~40 px below the
  // viewport top by the topbar. Without converting, a viewport top=100
  // becomes a wrapper-relative 100 which renders at viewport 140 = 40 px
  // jump. Subtract the parent's viewport offset to land where we expect.
  const rect = windowEl.getBoundingClientRect();
  const parent = windowEl.offsetParent as HTMLElement | null;
  const parentRect = parent
    ? parent.getBoundingClientRect()
    : { left: 0, top: 0 };

  // Clamp so the entire window stays inside the viewport — can't be
  // dragged off any edge. If the window is taller/wider than the
  // viewport (small monitor, big inventory), pin to top-left and let
  // the overflow stay off-screen rather than vanish entirely.
  const maxLeft = Math.max(0, window.innerWidth - rect.width);
  const maxTop = Math.max(0, window.innerHeight - rect.height);
  const clampedLeft = Math.max(0, Math.min(maxLeft, left));
  const clampedTop = Math.max(0, Math.min(maxTop, top));

  windowEl.style.left = `${clampedLeft - parentRect.left}px`;
  windowEl.style.top = `${clampedTop - parentRect.top}px`;
  windowEl.style.right = "auto";
  windowEl.style.bottom = "auto";
  // Some windows (vendor) use `transform: translate(-50%, -50%)` in CSS
  // to centre themselves. Once we anchor by left/top those transforms
  // become a -50%/-50% offset that yanks the window off-screen on the
  // first drag tick. Clear it so left/top become the authoritative anchor.
  windowEl.style.transform = "none";
}

function attachDrag(
  windowEl: HTMLElement,
  header: HTMLElement,
  storageKey: string,
): void {
  let dragging = false;
  let startX = 0;
  let startY = 0;
  let startLeft = 0;
  let startTop = 0;

  header.addEventListener("pointerdown", (e) => {
    if (locked) return;
    if (e.button !== 0) return; // left click only
    // Ignore clicks on the close button (and any future button in the header).
    if ((e.target as HTMLElement).closest("button")) return;
    const rect = windowEl.getBoundingClientRect();
    dragging = true;
    startX = e.clientX;
    startY = e.clientY;
    startLeft = rect.left;
    startTop = rect.top;
    header.setPointerCapture(e.pointerId);
    header.classList.add("dragging");
    e.preventDefault();
  });

  header.addEventListener("pointermove", (e) => {
    if (!dragging) return;
    applyPosition(windowEl, startLeft + (e.clientX - startX), startTop + (e.clientY - startY));
  });

  const endDrag = (e: PointerEvent): void => {
    if (!dragging) return;
    dragging = false;
    header.classList.remove("dragging");
    if (header.hasPointerCapture(e.pointerId)) {
      header.releasePointerCapture(e.pointerId);
    }
    const rect = windowEl.getBoundingClientRect();
    try {
      localStorage.setItem(
        POSITION_KEY_PREFIX + storageKey,
        JSON.stringify({ left: rect.left, top: rect.top }),
      );
    } catch {
      // Out of quota / disabled — no-op. Position resets on next load.
    }
  };
  header.addEventListener("pointerup", endDrag);
  header.addEventListener("pointercancel", endDrag);
}

function attachBringToFront(windowEl: HTMLElement): void {
  windowEl.addEventListener("pointerdown", () => {
    topZ++;
    windowEl.style.zIndex = String(topZ);
  });
}

// === Global helpers ===

export function setWindowsLocked(value: boolean): void {
  locked = value;
  for (const w of REGISTRY) {
    w.header.classList.toggle("locked", value);
    refreshLockIcon(w.lockBtn);
  }
}

export function areWindowsLocked(): boolean {
  return locked;
}

export function resetAllWindowPositions(): void {
  for (const w of REGISTRY) {
    try {
      localStorage.removeItem(POSITION_KEY_PREFIX + w.storageKey);
    } catch {
      // ignore
    }
    // Clear inline positioning so CSS defaults (top/right/etc.) apply again.
    w.windowEl.style.left = "";
    w.windowEl.style.top = "";
    w.windowEl.style.right = "";
    w.windowEl.style.bottom = "";
    w.windowEl.style.zIndex = "";
    w.windowEl.style.transform = ""; // restore CSS-defined centering if any
  }
  topZ = 30;
}

// Re-clamp every window when the viewport shrinks so a window doesn't
// vanish off-screen after a resize.
window.addEventListener("resize", () => {
  for (const w of REGISTRY) {
    const rect = w.windowEl.getBoundingClientRect();
    applyPosition(w.windowEl, rect.left, rect.top);
  }
});
